import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useRouteContext, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { Button, Drawer, EmptyState, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type MediaAssetDetails, type MediaMime, type MediaPreset } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { TagPicker } from '../tags/TagPicker.tsx';
import { CropDialog } from './CropDialog.tsx';
import { formatBytes, MIME_LABEL, nameOfFile, PRESETS, thumbUrl } from './format.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const ROUTE = '/env/$environmentId/content/media';

/** Content → Media Library (BB-52): upload, browse as a grid or list, filter, and manage one asset in a drawer. */
export function MediaLibraryPage() {
  const { environmentId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const { asset: openAssetId } = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: ROUTE });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('content.edit');
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'' | MediaMime>('');
  const [tag, setTag] = useState('');
  const [archived, setArchived] = useState(false);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const filters = { query, type, tag: tag.trim(), archived };
  const list = useQuery({ queryKey: ['media', environmentId, filters], queryFn: () => api.listMedia(environmentId, filters) });
  const uploads = useMutation({
    mutationFn: async (files: File[]) => {
      const results = [];
      for (const file of files) results.push({ file, result: await api.uploadMedia(environmentId, file, nameOfFile(file.name), file.name) });
      return results;
    },
    onSuccess: (results) => {
      const duplicates = results.filter((r) => r.result.deduplicated).map((r) => r.file.name);
      setNotice(duplicates.length ? `Already in the library: ${duplicates.join(', ')}` : `Uploaded ${results.length} ${results.length === 1 ? 'file' : 'files'}`);
      void queryClient.invalidateQueries({ queryKey: ['media', environmentId] });
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: ['media', environmentId] }),
  });
  const start = (files: FileList | null) => {
    if (files?.length) uploads.mutate([...files]);
  };
  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (canEdit) start(event.dataTransfer.files);
  };
  const openAsset = (id: string | undefined) => void navigate({ search: id ? { asset: id } : {} });
  const filtered = Boolean(query || type || tag.trim() || archived);

  return (
    <section>
      <h1>Media Library</h1>
      {!canEdit && <ReadOnlyBanner>Uploading and editing images needs the content.edit permission.</ReadOnlyBanner>}
      {canEdit && (
        <div
          className={dragging ? 'dropzone dropzone--active' : 'dropzone'}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <p>Drop PNG, JPEG, GIF or WebP files up to 5 MB here, or</p>
          <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple aria-label="Upload images" hidden onChange={(e) => start(e.target.files)} />
          <Button variant="primary" onClick={() => fileInput.current?.click()} disabled={uploads.isPending}>
            {uploads.isPending ? 'Uploading…' : 'Choose files'}
          </Button>
          {uploads.isError && <p role="alert">{errorText(uploads.error)}</p>}
          {notice && !uploads.isError && <p role="status">{notice}</p>}
        </div>
      )}
      <div className="filters">
        <label>
          Search <input aria-label="Search media" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label>
          Type{' '}
          <select aria-label="Filter by type" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <option value="">All types</option>
            {Object.entries(MIME_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tag <input aria-label="Filter by tag" value={tag} onChange={(e) => setTag(e.target.value)} />
        </label>
        <label>
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Archived
        </label>
        <div role="group" aria-label="View">
          <Button size="sm" variant={view === 'grid' ? 'primary' : 'secondary'} aria-pressed={view === 'grid'} onClick={() => setView('grid')}>
            Grid
          </Button>
          <Button size="sm" variant={view === 'list' ? 'primary' : 'secondary'} aria-pressed={view === 'list'} onClick={() => setView('list')}>
            List
          </Button>
        </div>
      </div>
      {list.isPending ? (
        <p aria-busy="true">Loading media…</p>
      ) : list.isError ? (
        <ErrorState title="Media could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title={archived ? 'No archived images' : 'No images yet'} description={filtered ? 'Nothing matches the filters.' : 'Upload banners and push images to use them in messages.'} />
      ) : view === 'grid' ? (
        <ul className="media-grid" aria-label="Images">
          {list.data.items.map((a) => (
            <li key={a.id}>
              <button type="button" className="media-card" onClick={() => openAsset(a.id)}>
                <img src={thumbUrl(a.current)} alt={a.altText ?? ''} loading="lazy" />
                <span>{a.name}</span>
                <span className="hint">
                  {MIME_LABEL[a.current.mime]} · {a.current.width}×{a.current.height} · {formatBytes(a.current.bytes)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Type</th>
              <th scope="col">Size</th>
              <th scope="col">Dimensions</th>
              <th scope="col">Author</th>
              <th scope="col">Uploaded</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((a) => (
              <tr key={a.id}>
                <td>
                  <button type="button" className="link-button" onClick={() => openAsset(a.id)}>
                    {a.name}
                  </button>
                </td>
                <td>{MIME_LABEL[a.current.mime]}</td>
                <td>{formatBytes(a.current.bytes)}</td>
                <td>
                  {a.current.width}×{a.current.height}
                </td>
                <td>{a.author}</td>
                <td>{a.createdAt.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {openAssetId && <AssetDrawer environmentId={environmentId} assetId={openAssetId} canEdit={canEdit} onClose={() => openAsset(undefined)} />}
    </section>
  );
}

function AssetDrawer({ environmentId, assetId, canEdit, onClose }: { environmentId: string; assetId: string; canEdit: boolean; onClose: () => void }) {
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const key = ['media-asset', environmentId, assetId];
  const asset = useQuery({ queryKey: key, queryFn: () => api.getMedia(environmentId, assetId) });
  const done = (data: MediaAssetDetails) => {
    queryClient.setQueryData(key, data);
    void queryClient.invalidateQueries({ queryKey: ['media', environmentId] });
  };
  const [name, setName] = useState('');
  const [altText, setAltText] = useState('');
  useEffect(() => {
    if (asset.data) {
      setName(asset.data.name);
      setAltText(asset.data.altText ?? '');
    }
  }, [asset.data]);
  const save = useMutation({ mutationFn: () => api.updateMedia(environmentId, assetId, { name: name.trim(), altText: altText.trim() || null }), onSuccess: done });
  const archive = useMutation({ mutationFn: () => (asset.data?.archivedAt ? api.restoreMedia(environmentId, assetId) : api.archiveMedia(environmentId, assetId)), onSuccess: done });
  const replace = useMutation({ mutationFn: (file: File) => api.replaceMedia(environmentId, assetId, file, file.name), onSuccess: done });
  const [cropping, setCropping] = useState<MediaPreset | null>(null);
  const crop = useMutation({
    mutationFn: (value: { preset: MediaPreset; crop: Parameters<typeof api.setMediaCrop>[4] }) => api.setMediaCrop(environmentId, assetId, current!.id, value.preset, value.crop),
    onSuccess: (data) => {
      done(data);
      setCropping(null);
    },
  });
  const replaceInput = useRef<HTMLInputElement>(null);
  const a = asset.data;
  const current = a?.versions[0];
  const dependents = archive.error instanceof ApiError ? ((archive.error.problem.dependents as Array<{ name: string; objectType: string }> | undefined) ?? []) : [];
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) save.mutate();
  };

  return (
    <Drawer open onOpenChange={(open) => !open && onClose()} title={a?.name ?? 'Image'}>
      {asset.isPending ? (
        <p aria-busy="true">Loading image…</p>
      ) : asset.isError || !a || !current ? (
        <ErrorState title="The image could not be loaded" message={errorText(asset.error)} />
      ) : (
        <>
          <img className="media-preview" src={current.url} alt={a.altText ?? ''} />
          {a.archivedAt && <p className="notice notice--warning">Archived {a.archivedAt.slice(0, 10)}: hidden from pickers; published URLs keep working.</p>}
          <dl className="details" aria-label="File details">
            <dt>Type</dt>
            <dd>{MIME_LABEL[current.mime]}{current.animated ? ' (animated)' : ''}</dd>
            <dt>Size</dt>
            <dd>{formatBytes(current.bytes)}</dd>
            <dt>Dimensions</dt>
            <dd>
              {current.width}×{current.height}
            </dd>
            <dt>Author</dt>
            <dd>{a.author}</dd>
            <dt>URL</dt>
            <dd>
              <code>{current.url}</code>
            </dd>
          </dl>
          <form aria-label="Image details" className="stack-form" onSubmit={submit}>
            <label>
              Name
              <input value={name} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              Alt text
              <input value={altText} disabled={!canEdit} onChange={(e) => setAltText(e.target.value)} />
            </label>
            {canEdit && (
              <Button type="submit" disabled={save.isPending || !name.trim()}>
                Save
              </Button>
            )}
            {save.isError && <span role="alert">{errorText(save.error)}</span>}
          </form>
          <TagPicker environmentId={environmentId} objectType="media_asset" objectId={a.id} canEdit={canEdit} />

          <h2>Renditions</h2>
          <table className="table" aria-label="Renditions">
            <thead>
              <tr>
                <th scope="col">Preset</th>
                <th scope="col">Status</th>
                <th scope="col">Size</th>
                {canEdit && <th scope="col">Crop</th>}
              </tr>
            </thead>
            <tbody>
              {PRESETS.map((p) => {
                const r = current.renditions[p.key];
                return (
                  <tr key={p.key}>
                    <td>{r?.url ? <a href={r.url}>{p.label}</a> : p.label}</td>
                    <td>{r?.status === 'skipped' ? 'Not for animations' : (r?.status ?? '—')}</td>
                    <td>{r?.width ? `${r.width}×${r.height}` : '—'}</td>
                    {canEdit && (
                      <td>
                        {p.aspect !== null && r && r.status !== 'skipped' && !a.archivedAt && (
                          <Button size="sm" onClick={() => setCropping(p.key)}>
                            Crop {p.label}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <h2>Versions</h2>
          <ol reversed aria-label="Versions">
            {a.versions.map((v) => (
              <li key={v.id}>
                v{v.version} · {MIME_LABEL[v.mime]} · {v.width}×{v.height} · {formatBytes(v.bytes)} · {v.author} · {v.createdAt.slice(0, 10)}
                {v.originalFileName ? ` · ${v.originalFileName}` : ''}
              </li>
            ))}
          </ol>

          <h2>Used in</h2>
          {a.usage.length === 0 ? (
            <p className="hint">Nothing uses this image yet.</p>
          ) : (
            <ul>
              {a.usage.map((u) => (
                <li key={`${u.objectType}:${u.objectId}`}>
                  {u.name} ({u.objectType})
                </li>
              ))}
            </ul>
          )}

          {canEdit && (
            <div className="filters" role="group" aria-label="Actions">
              {!a.archivedAt && (
                <>
                  <input ref={replaceInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" aria-label="Replace file" hidden onChange={(e) => e.target.files?.[0] && replace.mutate(e.target.files[0])} />
                  <Button onClick={() => replaceInput.current?.click()} disabled={replace.isPending}>
                    Replace file
                  </Button>
                </>
              )}
              <Button variant={a.archivedAt ? 'secondary' : 'danger'} onClick={() => archive.mutate()} disabled={archive.isPending}>
                {a.archivedAt ? 'Restore' : 'Archive'}
              </Button>
              {replace.isError && <p role="alert">{errorText(replace.error)}</p>}
              {archive.isError && (
                <div role="alert">
                  <p>{errorText(archive.error)}</p>
                  {dependents.length > 0 && (
                    <ul>
                      {dependents.map((d) => (
                        <li key={d.name}>
                          {d.name} ({d.objectType})
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          {cropping && (
            <CropDialog
              open
              onOpenChange={(open) => !open && setCropping(null)}
              version={current}
              preset={cropping}
              busy={crop.isPending}
              error={crop.isError ? errorText(crop.error) : null}
              onSave={(value) => crop.mutate({ preset: cropping, crop: value })}
            />
          )}
        </>
      )}
    </Drawer>
  );
}
